import styles from "./hero-style-2.module.css";
import type { CSSProperties } from "react";
import { HeroHeadingText } from "~/components/HeroHeadingText";
import { Heading, Flex, Grid, Box } from "@radix-ui/themes";
import {
  optimizedImagePath,
  optimizedImageSrcSet,
} from "~/utils/optimizedImage";

export function HeroStyle2({
  heroImageUrl,
  title,
  backgroundPosition = "50% 50%",
  mobileBackgroundPosition,
  showFullImageOnMobile = false,
  marginTop = "-25px",
  pageHeadingClassName,
  pageHeadingWrapperClassName,
}: {
  heroImageUrl: string;
  title: string;
  backgroundPosition?: string;
  /** Shift the phone crop without changing the image size or hero layout. */
  mobileBackgroundPosition?: string;
  /** Use the source photo's aspect ratio when a phone crop would cut out people. */
  showFullImageOnMobile?: boolean;
  marginTop?: string;
  pageHeadingClassName?: string;
  pageHeadingWrapperClassName?: string;
}) {
  const canUseOptimizedHero =
    heroImageUrl.startsWith("/") && /\.(jpe?g|png)$/i.test(heroImageUrl);
  const heroSrc = canUseOptimizedHero
    ? optimizedImagePath(heroImageUrl, 1170)
    : heroImageUrl;
  const heroSrcSet = canUseOptimizedHero
    ? optimizedImageSrcSet(heroImageUrl, [780, 1170, 1600])
    : undefined;

  return (
    <Grid
      className={`${styles.hero} ${showFullImageOnMobile ? styles.fullImageOnMobile : ""}`}
      style={{ "--hero-margin-top": marginTop } as CSSProperties}
      columns="24"
      rows={{ initial: "19", sm: "24" }}
      height="calc(100dvh - 85px)"
      width="100dvw"
    >
      <Box
        gridColumn={{ initial: "1 / 25" }}
        gridRow={{ initial: "1/20", sm: "1 / 25" }}
        className={styles.heroImage}
        asChild
      >
        <img
          src={heroSrc}
          srcSet={heroSrcSet}
          sizes="100vw"
          width="1170"
          height="781"
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          decoding="async"
          style={{
            "--hero-position": backgroundPosition,
            "--hero-phone-position": mobileBackgroundPosition,
          } as CSSProperties}
        />
      </Box>
      <Box
        gridColumn={{ initial: "1 / 25" }}
        gridRow={{ initial: "1/20", sm: "1 / 25" }}
        className={styles.heroOverlay}
      />
      <Flex
        gridColumn="1/25"
        gridRow="1/25"
        justify="center"
        align="end"
        className={`${styles.pageHeadingWrapper} ${pageHeadingWrapperClassName ?? ""}`}
      >
        <Box className={styles.pageHeadingContainer}>
          <Heading
            className={`${styles.pageHeading} ${pageHeadingClassName ?? ""}`}
            data-testid="hero-heading"
          >
            <HeroHeadingText title={title} />
          </Heading>
        </Box>
      </Flex>
    </Grid>
  );
}
